import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Volume2, VolumeX, Loader2, Square, EyeOff } from "lucide-react";
import { api, USER_ID, track, mkAudio } from "./api";
import { useSound } from "./sound";
import { useLang, useT } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FACE = "/mascot/face.png";
const HIDE_KEY = "xp_mascot_hidden";
const MUTE_KEY = "xp_mascot_mute";
const GREET_KEY = "xp_mascot_greeted";

export const mascotHidden = () => localStorage.getItem(HIDE_KEY) === "1";
export const setMascotHidden = (v) => {
  localStorage.setItem(HIDE_KEY, v ? "1" : "0");
  window.dispatchEvent(new Event("xp-mascot-pref"));
};

// Phase 5 (owner-approved): home, menu, dish, quiz + passport pages
const enabledOn = (path) =>
  path === "/experience" || path === "/menu" || path === "/experience/quiz" ||
  path.startsWith("/experience/dish/") || path === "/experience/passport";

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

// Skeletal 2D rig — the character is layered (torso + jointed head + arms + legs) and animated
// with smooth continuous CSS motion, like a rigged cartoon. No frame swapping.
const RIGS = {
  wave: {
    size: [257, 480],
    body: "/mascot/rig/wave_torso.png",
    parts: [
      { src: "/mascot/rig/wave_head.png", origin: "48.6% 22.5%", cls: "rig-head-nod" },
      { src: "/mascot/rig/wave_arm.png", origin: "33% 34%", cls: "rig-arm-wave" },
    ],
    bodyCls: "rig-sway",
  },
  celebrate: {
    size: [392, 480],
    body: "/mascot/rig/celebrate_torso.png",
    parts: [
      { src: "/mascot/rig/celebrate_head.png", origin: "57.4% 33.3%", cls: "rig-head-tilt" },
      { src: "/mascot/rig/celebrate_arml.png", origin: "48% 37%", cls: "rig-arm-cane" },
      { src: "/mascot/rig/celebrate_armr.png", origin: "73% 37%", cls: "rig-arm-fist" },
    ],
    bodyCls: "rig-bounce",
  },
  thumbsup: {
    size: [224, 480],
    body: "/mascot/rig/thumbsup_torso.png",
    parts: [{ src: "/mascot/rig/thumbsup_head.png", origin: "53.6% 21.9%", cls: "rig-head-nod" }],
    bodyCls: "rig-nod",
  },
  idle: { full: "/mascot/idle.png", cls: "rig-sway" },
};

// dignified stroll: torso (with cane), stepping legs, nodding head
const Walker = ({ walk }) => (
  <div className="fixed inset-x-0 bottom-[86px] z-[54] pointer-events-none">
    <div
      data-testid="mascot-walker"
      className="mascot-walker absolute bottom-0 w-[68px] md:w-[82px]"
      style={{ left: walk.left, "--walk-dist": `${walk.dist}px`, "--walk-ms": `${walk.ms}ms` }}
    >
      <div className="rig-walk-bob relative w-full" style={{ aspectRatio: "227 / 480" }}>
        <img src="/mascot/rig/walk_torso.png" alt="VeerGi strolling in" draggable="false" className="absolute inset-0 w-full h-full" />
        <img src="/mascot/rig/walk_legb.png" alt="" draggable="false" className="absolute inset-0 w-full h-full rig-leg-back" style={{ transformOrigin: "31.7% 83.3%" }} />
        <img src="/mascot/rig/walk_legf.png" alt="" draggable="false" className="absolute inset-0 w-full h-full rig-leg-front" style={{ transformOrigin: "74% 83.3%" }} />
        <img src="/mascot/rig/walk_head.png" alt="" draggable="false" className="absolute inset-0 w-full h-full rig-head-step" style={{ transformOrigin: "55% 18.3%" }} />
      </div>
    </div>
  </div>
);

const VeerGiRig = ({ pose, className, onError }) => {
  const rig = RIGS[pose] || RIGS.idle;
  const still = reducedMotion();
  if (!rig.parts) {
    return (
      <img src={rig.full} alt="VeerGi mascot" onError={onError} draggable="false"
        className={`${className} ${still ? "" : rig.cls}`} />
    );
  }
  return (
    <div className={`relative shrink-0 ${className} ${still ? "" : rig.bodyCls}`}
      style={{ aspectRatio: `${rig.size[0]} / ${rig.size[1]}` }}>
      <img src={rig.body} alt="VeerGi mascot" onError={onError} draggable="false"
        className="absolute inset-0 w-full h-full" />
      {rig.parts.map((p) => (
        <img key={p.src} src={p.src} alt="" draggable="false"
          className={`absolute inset-0 w-full h-full ${still ? "" : p.cls}`}
          style={{ transformOrigin: p.origin }} />
      ))}
    </div>
  );
};

const Confetti = () => (
  <div className="mascot-confetti fixed inset-0 z-[54] pointer-events-none" aria-hidden="true">
    {Array.from({ length: 22 }).map((_, i) => (
      <span
        key={i}
        style={{
          left: `${(i * 41 + 7) % 100}%`,
          animationDelay: `${(i % 8) * 0.12}s`,
          background: ["#D4AF37", "#F0C742", "#E25822", "#FFFFFF"][i % 4],
        }}
      />
    ))}
  </div>
);

export default function Mascot() {
  const location = useLocation();
  const t = useT();
  const { lang } = useLang();
  const { voiceReady, rawSoundOn } = useSound();
  const [hidden, setHidden] = useState(mascotHidden);
  const [imgOk, setImgOk] = useState(true);
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(null);
  const [bounce, setBounce] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const [voice, setVoice] = useState("idle");
  const [walk, setWalk] = useState(null);
  const walkRef = useRef(false);
  const timers = useRef([]);
  const audioRef = useRef(null);
  const enabled = enabledOn(location.pathname);

  const later = useCallback((fn, ms) => timers.current.push(setTimeout(fn, ms)), []);

  // short spoken reaction — only when device sound is ON and mascot isn't muted (never autoplays otherwise)
  const speakLine = useCallback(async (kind) => {
    if (!voiceReady || !rawSoundOn || muted) return;
    try {
      const r = await api.post("/mascot/speak", { user_id: USER_ID, kind, language: lang });
      if (!r.data.audio_url) return;
      audioRef.current?.pause();
      const a = mkAudio(`${BACKEND_URL}${r.data.audio_url}`);
      audioRef.current = a;
      a.play().catch(() => {});
    } catch {}
  }, [voiceReady, rawSoundOn, muted, lang]);

  useEffect(() => {
    const sync = () => setHidden(mascotHidden());
    window.addEventListener("xp-mascot-pref", sync);
    return () => window.removeEventListener("xp-mascot-pref", sync);
  }, []);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    audioRef.current?.pause();
  }, []);

  // first-visit greeting bubble (per session, dismissible, no autoplay sound)
  useEffect(() => {
    if (!enabled || hidden || sessionStorage.getItem(GREET_KEY) === "1") return;
    const id = setTimeout(() => {
      if (walkRef.current) return;
      sessionStorage.setItem(GREET_KEY, "1");
      setBubble({ pose: "wave", key: "mascot.greet" });
      track("mascot_greeting_shown", {});
      later(() => setBubble((b) => (b?.key === "mascot.greet" ? null : b)), 9000);
    }, 1500);
    return () => clearTimeout(id);
  }, [enabled, hidden, later]);

  // reactions: dish added to order list / reward redeemed / quiz answered correctly
  useEffect(() => {
    // big moments: VeerGi strolls in from the edge with his cane, then celebrates
    const bigMoment = (key, kind) => {
      const finish = () => {
        setBubble({ pose: "celebrate", key });
        speakLine(kind);
        if (!reducedMotion()) {
          setCelebrating(true);
          later(() => setCelebrating(false), 2700);
        }
        later(() => setBubble((b) => (b?.key === key ? null : b)), 5000);
      };
      setOpen(false);
      setBubble(null);
      if (reducedMotion()) return finish();
      if (walkRef.current) return finish();
      walkRef.current = true;
      const end = Math.max(window.innerWidth - 130, 40);
      const dist = Math.min(end + 90, 1100);
      const ms = Math.min(Math.round(dist * 6.2), 4800);
      setWalk({ left: end - dist, dist, ms });
      later(() => {
        setWalk(null);
        walkRef.current = false;
        finish();
      }, ms + 150);
    };
    const onEvent = (e) => {
      if (hidden || !enabledOn(window.location.pathname)) return;
      const { type } = e.detail || {};
      if (type === "dish-added") {
        setOpen(false);
        setBubble({ pose: "thumbsup", key: "mascot.dishAdded" });
        setBounce(true);
        speakLine("dish-added");
        later(() => setBounce(false), 800);
        later(() => setBubble((b) => (b?.key === "mascot.dishAdded" ? null : b)), 3500);
      } else if (type === "quiz-correct") {
        setOpen(false);
        setBubble({ pose: "thumbsup", key: "mascot.quizCorrect" });
        setBounce(true);
        speakLine("quiz-correct");
        later(() => setBounce(false), 800);
        later(() => setBubble((b) => (b?.key === "mascot.quizCorrect" ? null : b)), 3500);
      } else if (type === "reward") {
        bigMoment("mascot.reward", "reward");
        track("mascot_reward_celebration", {});
      } else if (type === "quiz-level-complete") {
        bigMoment("mascot.levelDone", "level-complete");
        track("mascot_level_celebration", {});
      } else if (type === "champion") {
        bigMoment("mascot.champion", "champion");
        track("mascot_champion_celebration", {});
      } else if (type === "hint") {
        setBounce(true);
        later(() => setBounce(false), 800);
      }
    };
    window.addEventListener("xp-mascot", onEvent);
    return () => window.removeEventListener("xp-mascot", onEvent);
  }, [hidden, later, speakLine]);

  const stopVoice = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setVoice("idle");
  };

  const speak = async () => {
    if (voice === "playing") return stopVoice();
    setVoice("loading");
    try {
      track("mascot_voice_played", { language: lang });
      const r = await api.post("/greeting/speak", { user_id: USER_ID, language: lang });
      if (!r.data.audio_url) return setVoice("idle");
      // one reusable element — iPad Safari blocks a second Audio() started outside the tap gesture
      const a = mkAudio("");
      audioRef.current = a;
      const mainUrl = `${BACKEND_URL}${r.data.audio_url}`;
      const playMain = () => {
        a.onended = () => setVoice("idle");
        a.src = mainUrl;
        a.play().catch(() => setVoice("idle"));
      };
      if (r.data.prelude_url) {
        a.onended = playMain;
        a.src = `${BACKEND_URL}${r.data.prelude_url}`;
      } else {
        a.onended = () => setVoice("idle");
        a.src = mainUrl;
      }
      await a.play();
      setVoice("playing");
    } catch {
      setVoice("idle");
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      localStorage.setItem(MUTE_KEY, m ? "0" : "1");
      if (!m) stopVoice();
      return !m;
    });
  };

  const hideMe = () => {
    stopVoice();
    setOpen(false);
    setBubble(null);
    setMascotHidden(true);
    track("mascot_hidden", {});
  };

  const toggleOpen = () => {
    setBubble(null);
    setOpen((o) => {
      if (o) stopVoice();
      else track("mascot_open", { path: location.pathname });
      return !o;
    });
  };

  if (!enabled || !imgOk) return null;

  if (hidden) {
    return (
      <div className="fixed bottom-[86px] z-[55] w-full max-w-md pointer-events-none flex justify-end px-3 md:w-auto md:max-w-none md:px-0 md:right-5">
        <button
          data-testid="mascot-restore-btn"
          onClick={() => { setMascotHidden(false); track("mascot_restored", { path: location.pathname }); }}
          className="pointer-events-auto text-[9px] font-bold tracking-[0.2em] text-white/40 hover:text-[#D4AF37] border border-white/10 rounded-full px-3 py-1.5 bg-black/50 backdrop-blur-md"
        >
          SHOW VEERGI
        </button>
      </div>
    );
  }

  return (
    <>
      {celebrating && <Confetti />}
      {walk && <Walker walk={walk} />}
      <div
        data-testid="mascot-root"
        className="fixed bottom-[86px] z-[55] w-full max-w-md pointer-events-none flex flex-col items-end px-3 md:w-auto md:max-w-none md:px-0 md:right-5"
      >
        {bubble && !open && (
          <div
            data-testid="mascot-bubble"
            className="mascot-pop pointer-events-auto mb-2 max-w-[280px] rounded-2xl rounded-br-md border border-[#D4AF37]/30 bg-[#141414]/95 backdrop-blur-xl shadow-2xl p-3 flex items-start gap-3"
          >
            <VeerGiRig pose={bubble.pose} className="h-24" onError={() => setImgOk(false)} />
            <div className="min-w-0 flex-1">
              <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-bold">VEERGI</p>
              <p className="text-xs text-white/85 leading-snug mt-0.5">{t(bubble.key)}</p>
            </div>
            <button data-testid="mascot-bubble-close" onClick={() => setBubble(null)} aria-label="Dismiss" className="shrink-0 -mt-0.5">
              <X size={14} className="text-white/50" />
            </button>
          </div>
        )}
        {open && (
          <div
            data-testid="mascot-panel"
            className="mascot-pop pointer-events-auto mb-2 w-[272px] rounded-2xl rounded-br-md border border-[#D4AF37]/30 bg-[#141414]/95 backdrop-blur-xl shadow-2xl p-3.5 text-left"
          >
            <div className="flex items-start gap-3">
              <VeerGiRig pose="wave" className="h-24" onError={() => setImgOk(false)} />
              <div className="min-w-0 flex-1">
                <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-bold">VEERGI</p>
                <p className="text-xs text-white/85 leading-snug mt-1">{t("mascot.greet")}</p>
              </div>
              <button data-testid="mascot-panel-close" onClick={toggleOpen} aria-label="Close" className="shrink-0 -mt-0.5">
                <X size={15} className="text-white/50" />
              </button>
            </div>
            {voiceReady && !muted && (
              <button
                data-testid="mascot-hear-btn"
                onClick={speak}
                className="w-full mt-3 bg-[#D4AF37] hover:bg-[#F0C742] text-black font-bold rounded-full py-2 text-[11px] tracking-widest flex items-center justify-center gap-1.5"
              >
                {voice === "loading" ? <Loader2 size={13} className="animate-spin" /> : voice === "playing" ? <Square size={11} /> : <Volume2 size={13} />}
                {voice === "playing" ? t("dish.stop") : t("mascot.hear")}
              </button>
            )}
            <div className="flex items-center justify-between mt-2.5">
              {voiceReady ? (
                <button data-testid="mascot-mute-btn" onClick={toggleMute} className="flex items-center gap-1 text-[10px] tracking-widest text-white/50">
                  {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                  {muted ? t("mascot.unmute") : t("mascot.mute")}
                </button>
              ) : <span />}
              <button data-testid="mascot-hide-btn" onClick={hideMe} className="flex items-center gap-1 text-[10px] tracking-widest text-white/50">
                <EyeOff size={12} />
                {t("mascot.hide")}
              </button>
            </div>
          </div>
        )}
        <button
          data-testid="mascot-btn"
          onClick={toggleOpen}
          aria-label="VeerGi mascot"
          className={`mascot-btn pointer-events-auto relative w-14 h-14 md:w-[68px] md:h-[68px] rounded-full overflow-hidden border border-[#D4AF37]/40 bg-[#141414]/90 shadow-[0_4px_20px_rgba(0,0,0,0.55)] ${bounce ? "mascot-bounce" : ""}`}
        >
          <img src={FACE} alt="VeerGi mascot" className="w-full h-full object-cover object-top" onError={() => setImgOk(false)} />
          {bounce && <span className="mascot-spark" />}
        </button>
      </div>
    </>
  );
}
